import React from 'react';
import { Flame, Shield } from 'lucide-react';
import { getLocalCalendarDay } from '../../utils/dateUtils';

const MenuStreak = ({ streak, lastPlayedDate, freezeCount = 0 }) => {
    const today = getLocalCalendarDay();
    const isExtendedToday = lastPlayedDate === today;
    const hasStreak = streak > 0;
    const isHighStreak = streak >= 5;
    const isSuperStreak = streak >= 10;
    const hasFreeze = freezeCount > 0;

    return (
        <div
            className={`relative flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-500 overflow-hidden group ${isExtendedToday
                ? isHighStreak
                    ? 'bg-warning/20 border-warning text-warning shadow-[0_0_15px_rgba(var(--color-warning-rgb),0.5)] scale-105'
                    : 'bg-warning/10 border-warning/50 text-warning shadow-[0_0_10px_rgba(var(--color-warning-rgb),0.2)]'
                : hasStreak
                    ? 'bg-surface border-border text-muted hover:border-primary/30'
                    : 'bg-surface border-border text-main/50'
                }`}
            title={
                isExtendedToday
                    ? `Streak extended! ${freezeCount > 0 ? `${freezeCount} freeze${freezeCount > 1 ? 's' : ''} available` : 'No freezes'}`
                    : `Play to extend your streak! ${freezeCount > 0 ? `${freezeCount} freeze${freezeCount > 1 ? 's' : ''} protect you` : 'No freeze protection'}`
            }
        >
            {/* Background Animation for High Streak */}
            {isExtendedToday && isHighStreak && (
                <div className="absolute inset-0 bg-gradient-to-r from-warning/20 via-error/20 to-warning/20 animate-[gradient_2s_ease_infinite] bg-[length:200%_200%]"></div>
            )}

            {/* Flame Icon */}
            <div className={`relative ${isHighStreak && isExtendedToday ? 'animate-bounce-slight' : ''}`}>
                <Flame
                    size={isHighStreak ? 20 : 18}
                    className={`transition-all duration-300 ${isExtendedToday
                        ? isHighStreak
                            ? 'fill-warning text-warning drop-shadow-[0_0_8px_rgba(var(--color-warning-rgb),0.8)] animate-[pulse_0.5s_ease-in-out_infinite]'
                            : 'fill-warning text-warning animate-pulse'
                        : hasStreak
                            ? 'text-primary/70'
                            : 'text-main/50'
                        }`}
                />
                {/* Extra Flame Particles for Super Streak */}
                {isExtendedToday && isSuperStreak && (
                    <>
                        <div className="absolute -top-1 left-0 w-1 h-1 bg-warning rounded-full animate-[ping_1s_infinite]"></div>
                        <div className="absolute top-0 right-0 w-1 h-1 bg-error rounded-full animate-[ping_1.2s_infinite_0.3s]"></div>
                    </>
                )}
            </div>

            {/* Streak Count */}
            <span className={`font-bold text-sm relative z-10 ${isExtendedToday
                ? isHighStreak
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-warning to-error drop-shadow-sm'
                    : 'text-primary'
                : 'text-muted'
                }`}>
                {streak || 0}
            </span>

            {/* Fire Text Suffix for High Streak */}
            {isExtendedToday && isHighStreak && (
                <span className="text-[10px] font-black text-warning uppercase tracking-tighter animate-pulse">
                    ON FIRE!
                </span>
            )}

            {/* Divider • Freeze Section */}
            {streak > 0 && (
                <>
                    <span className="text-border/50 text-xs mx-0.5">•</span>

                    {/* Shield Icon with Count */}
                    <div className="flex items-center gap-1">
                        <Shield
                            size={14}
                            className={`transition-all duration-300 ${hasFreeze
                                ? 'fill-accent/50 text-accent'
                                : 'fill-none text-muted/50'
                                }`}
                        />
                        <span className={`text-xs font-semibold ${hasFreeze ? 'text-accent' : 'text-muted/50'
                            }`}>
                            {freezeCount}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export default MenuStreak;
